import React, { ChangeEvent, ClipboardEvent, FC, useEffect, useState } from 'react';
import { getNormalizedPhoneLength } from '~/src/helpers/get-normalized-phone-length';
import { validatePhone } from '~/src/helpers/inputs-validators';
import Textbox from '~/src/components/form-components/textbox/textbox';
import { formattedPhoneValue } from './phone-field.utils';
import {
    AUTO_MASK_LENGTH,
    PHONE_ERROR,
    PHONE_MASK,
} from '~/src/components/common-components/phone-field/phone-field.constants';
import type { PhoneFieldProps } from './phone-field.types';

export const PhoneField: FC<PhoneFieldProps> = ({
    onOnceSuccess,
    error,
    backendError,
    onBackendErrorClear,
    ...props
}) => {
    const [phoneValue, setPhoneValue] = useState('');
    const [onceSuccessCompleted, setOnceSuccessCompleted] = useState(false);
    const [phoneDataInserted, setPhoneDataInserted] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [isValidatedPhone, setIsValidatedPhone] = useState(false);

    useEffect(() => {
        const phoneValueLength=getNormalizedPhoneLength(phoneValue);

        if (!onceSuccessCompleted && !phoneError && phoneValueLength > AUTO_MASK_LENGTH && onOnceSuccess) {
            setOnceSuccessCompleted(true);
            onOnceSuccess();
        }
    }, [phoneValue, phoneError]);

    const clearBackendError = () => {
        backendError && onBackendErrorClear?.();
    };

    const onPhoneErrorHandler = (value: string) => {
        const isValidPhone = validatePhone(value);
        !isValidPhone && setPhoneError(error || PHONE_ERROR);
        (isValidPhone || !value || value.length <= 6) && setPhoneError('');
        setIsValidatedPhone(isValidPhone);
    };

    const handlePhoneDataInsertion = (value: string) => {
        setPhoneDataInserted(true);
        clearBackendError();
        const valuePure = formattedPhoneValue(value);
        setPhoneValue(valuePure);
        onPhoneErrorHandler(valuePure);
    };

    const onPhoneValuePasteHandler = (event: ClipboardEvent<HTMLInputElement>) => {
        const value = event?.clipboardData?.getData('text');
        handlePhoneDataInsertion(value || '');
    };

    const onPhoneValueDropHandler = (event: React.DragEvent<HTMLInputElement>) => {
        const value = event?.dataTransfer?.getData('text');
        handlePhoneDataInsertion(value || '');
    };

    const onPhoneValueChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        clearBackendError();
        if (!phoneDataInserted) {
            const value = event.target.value || '';
            setPhoneValue(value);
            value.length > AUTO_MASK_LENGTH && onPhoneErrorHandler(value);
        }
        setPhoneDataInserted(false);
    };

    const onClearHandler = () => {
        setPhoneValue('');
        clearBackendError();
    };

    return (
        <Textbox
            value={phoneValue}
            onValueChange={onPhoneValueChangeHandler}
            onValuePaste={onPhoneValuePasteHandler}
            onValueDrop={onPhoneValueDropHandler}
            onClear={onClearHandler}
            mask={PHONE_MASK}
            error={phoneError || backendError}
            validated={isValidatedPhone}
            {...props}
        />
    );
};
